/**
 * Textes de la zone « Satisfaction » (clés préfixées `satisfaction.`), fusionnés dans les dictionnaires par i18n/locales/*.ts.
 * Les trois langues doivent fournir exactement les mêmes clés et variables (vérifié par les tests i18n).
 */
export const satisfactionFr = {
  'satisfaction.title': 'Satisfaction des clients',
  'satisfaction.description': 'Notes et commentaires que les clients laissent en fin de session, par technicien.',
  'satisfaction.kpis': 'Indicateurs de satisfaction',
  'satisfaction.outOf5': '{value} sur 5',
  'satisfaction.teamAverage': 'Note moyenne ce mois-ci',
  'satisfaction.teamAverageHint': 'Sur 12 mois : {value}',
  'satisfaction.monthCount': 'Notes ce mois-ci',
  'satisfaction.monthCountHint': 'Mois précédent : {count}',
  'satisfaction.variation': 'Variation de la moyenne',
  'satisfaction.variationValue': '{value} pt',
  'satisfaction.variationHint': 'Par rapport au mois précédent',
  'satisfaction.noComparison': 'Pas de note le mois précédent',
  'satisfaction.technicians': 'Techniciens',
  'satisfaction.techniciansDesc': 'Moyenne sur 12 mois et évolution du mois en cours. Cliquez sur une ligne pour ouvrir le détail.',
  'satisfaction.table': 'Satisfaction par technicien',
  'satisfaction.col.name': 'Technicien',
  'satisfaction.col.average': 'Moyenne (12 mois)',
  'satisfaction.col.count': 'Notes',
  'satisfaction.col.current': 'Ce mois-ci',
  'satisfaction.col.delta': 'Évolution',
  'satisfaction.delta.up': 'En hausse de {value} point',
  'satisfaction.delta.down': 'En baisse de {value} point',
  'satisfaction.delta.flat': 'Stable',
  'satisfaction.open': 'Ouvrir le détail de {name}',
  'satisfaction.unknown': 'Technicien inconnu',
  'satisfaction.empty.title': 'Aucune note pour le moment',
  'satisfaction.empty.description': 'Les notes apparaissent ici dès que des clients évaluent leurs sessions.',
  'satisfaction.back': 'Retour à la satisfaction',
  'satisfaction.detail.average': 'Moyenne sur 12 mois',
  'satisfaction.detail.count': '{count} note(s) sur 12 mois',
  'satisfaction.chart.title': 'Évolution mensuelle',
  'satisfaction.chart.desc': 'Nombre de notes (barres) et note moyenne (ligne) sur 12 mois.',
  'satisfaction.chart.aria': 'Évolution mensuelle : {count} note(s) sur 12 mois',
  'satisfaction.chart.month': 'Mois',
  'satisfaction.chart.count': 'Notes',
  'satisfaction.chart.average': 'Moyenne',
  'satisfaction.ratings.title': 'Notes et commentaires',
  'satisfaction.ratings.desc': 'Les plus récents d\'abord.',
  'satisfaction.ratings.list': 'Liste des notes',
  'satisfaction.ratings.noComment': 'Sans commentaire',
  'satisfaction.ratings.openSession': 'Voir la session',
  'satisfaction.ratings.empty': 'Aucune note pour ce technicien.',
  'satisfaction.ratings.shown': '{shown} note(s) affichée(s) sur {total}',
  'satisfaction.ratings.more': 'Afficher plus',
} as const;

type SatisfactionKey = keyof typeof satisfactionFr;

export const satisfactionEn: Record<SatisfactionKey, string> = {
  'satisfaction.title': 'Customer satisfaction',
  'satisfaction.description': 'Ratings and comments customers leave at the end of a session, per technician.',
  'satisfaction.kpis': 'Satisfaction indicators',
  'satisfaction.outOf5': '{value} out of 5',
  'satisfaction.teamAverage': 'Average rating this month',
  'satisfaction.teamAverageHint': 'Over 12 months: {value}',
  'satisfaction.monthCount': 'Ratings this month',
  'satisfaction.monthCountHint': 'Previous month: {count}',
  'satisfaction.variation': 'Change in average',
  'satisfaction.variationValue': '{value} pt',
  'satisfaction.variationHint': 'Compared with the previous month',
  'satisfaction.noComparison': 'No rating in the previous month',
  'satisfaction.technicians': 'Technicians',
  'satisfaction.techniciansDesc': '12-month average and current month trend. Click a row to open the details.',
  'satisfaction.table': 'Satisfaction per technician',
  'satisfaction.col.name': 'Technician',
  'satisfaction.col.average': 'Average (12 months)',
  'satisfaction.col.count': 'Ratings',
  'satisfaction.col.current': 'This month',
  'satisfaction.col.delta': 'Trend',
  'satisfaction.delta.up': 'Up {value} point',
  'satisfaction.delta.down': 'Down {value} point',
  'satisfaction.delta.flat': 'Stable',
  'satisfaction.open': 'Open details for {name}',
  'satisfaction.unknown': 'Unknown technician',
  'satisfaction.empty.title': 'No ratings yet',
  'satisfaction.empty.description': 'Ratings appear here as soon as customers rate their sessions.',
  'satisfaction.back': 'Back to satisfaction',
  'satisfaction.detail.average': '12-month average',
  'satisfaction.detail.count': '{count} rating(s) over 12 months',
  'satisfaction.chart.title': 'Monthly trend',
  'satisfaction.chart.desc': 'Number of ratings (bars) and average rating (line) over 12 months.',
  'satisfaction.chart.aria': 'Monthly trend: {count} rating(s) over 12 months',
  'satisfaction.chart.month': 'Month',
  'satisfaction.chart.count': 'Ratings',
  'satisfaction.chart.average': 'Average',
  'satisfaction.ratings.title': 'Ratings and comments',
  'satisfaction.ratings.desc': 'Most recent first.',
  'satisfaction.ratings.list': 'Ratings list',
  'satisfaction.ratings.noComment': 'No comment',
  'satisfaction.ratings.openSession': 'View session',
  'satisfaction.ratings.empty': 'No ratings for this technician.',
  'satisfaction.ratings.shown': '{shown} of {total} rating(s) shown',
  'satisfaction.ratings.more': 'Show more',
};

export const satisfactionAr: Record<SatisfactionKey, string> = {
  'satisfaction.title': 'رضا العملاء',
  'satisfaction.description': 'التقييمات والتعليقات التي يتركها العملاء في نهاية الجلسة، حسب كل فني.',
  'satisfaction.kpis': 'مؤشرات الرضا',
  'satisfaction.outOf5': '{value} من 5',
  'satisfaction.teamAverage': 'متوسط التقييم هذا الشهر',
  'satisfaction.teamAverageHint': 'خلال 12 شهرًا: {value}',
  'satisfaction.monthCount': 'التقييمات هذا الشهر',
  'satisfaction.monthCountHint': 'الشهر السابق: {count}',
  'satisfaction.variation': 'تغيّر المتوسط',
  'satisfaction.variationValue': '{value} نقطة',
  'satisfaction.variationHint': 'مقارنة بالشهر السابق',
  'satisfaction.noComparison': 'لا يوجد تقييم في الشهر السابق',
  'satisfaction.technicians': 'الفنيون',
  'satisfaction.techniciansDesc': 'المتوسط خلال 12 شهرًا وتطور الشهر الحالي. انقر على سطر لفتح التفاصيل.',
  'satisfaction.table': 'الرضا حسب الفني',
  'satisfaction.col.name': 'الفني',
  'satisfaction.col.average': 'المتوسط (12 شهرًا)',
  'satisfaction.col.count': 'التقييمات',
  'satisfaction.col.current': 'هذا الشهر',
  'satisfaction.col.delta': 'التطور',
  'satisfaction.delta.up': 'ارتفاع بمقدار {value} نقطة',
  'satisfaction.delta.down': 'انخفاض بمقدار {value} نقطة',
  'satisfaction.delta.flat': 'مستقر',
  'satisfaction.open': 'فتح تفاصيل {name}',
  'satisfaction.unknown': 'فني غير معروف',
  'satisfaction.empty.title': 'لا توجد تقييمات حاليًا',
  'satisfaction.empty.description': 'تظهر التقييمات هنا بمجرد أن يقيّم العملاء جلساتهم.',
  'satisfaction.back': 'العودة إلى الرضا',
  'satisfaction.detail.average': 'المتوسط خلال 12 شهرًا',
  'satisfaction.detail.count': '{count} تقييم خلال 12 شهرًا',
  'satisfaction.chart.title': 'التطور الشهري',
  'satisfaction.chart.desc': 'عدد التقييمات (أعمدة) ومتوسط التقييم (خط) خلال 12 شهرًا.',
  'satisfaction.chart.aria': 'التطور الشهري: {count} تقييم خلال 12 شهرًا',
  'satisfaction.chart.month': 'الشهر',
  'satisfaction.chart.count': 'التقييمات',
  'satisfaction.chart.average': 'المتوسط',
  'satisfaction.ratings.title': 'التقييمات والتعليقات',
  'satisfaction.ratings.desc': 'الأحدث أولًا.',
  'satisfaction.ratings.list': 'قائمة التقييمات',
  'satisfaction.ratings.noComment': 'بدون تعليق',
  'satisfaction.ratings.openSession': 'عرض الجلسة',
  'satisfaction.ratings.empty': 'لا توجد تقييمات لهذا الفني.',
  'satisfaction.ratings.shown': 'عرض {shown} من {total} تقييم',
  'satisfaction.ratings.more': 'عرض المزيد',
};
